// ============================================================================
// The subcontractor tier roster — the data half.
//
// ONE source: v_subcontractor_tier. Tier 1 is conferred and stored; 2, 3 and 4
// are derived in the view and never stored, so nothing here recomputes them —
// ⛔ a second copy of the thresholds in TypeScript is exactly how the sourcing
// board drifted from its own CHECK constraint.
//
// The view is scoped to ACTIVE field subcontractors and includes the ones with
// no active facility: a sub who has just lost their last building is the
// highest-risk row on this screen, and until 2026-09-11 an inner join dropped
// them from it entirely.
//
// Account-manager names resolve through v_am_name (shared/am-name), which is the
// resolver — pickers exclude, resolvers resolve (TCSD-0129) — so a former
// employee's name still renders.
//
// Flat-fetch + client-join, house style. No TanStack Query.
// ============================================================================
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';
import { fetchAmNameMap } from '../../../shared/am-name';

export interface SubTierRow {
  provider_id: string;
  display_name: string;
  tier: 1 | 2 | 3 | 4;
  active_facility_count: number;
  /** NULL when the provider has no TCS start date on file at all. */
  days_with_tcs: number | null;
  /** true = the start date is an estimate, so the tenure is too. */
  tenure_is_estimate: boolean;
  /** DISTINCT BUILDINGS lost in the last 60 days. */
  facilities_lost_60d: number;
  /** How many SEPARATE handovers that was. One incoming sub on one day is one
   *  event however many buildings moved. */
  loss_events_60d: number;
  /** True when one of those events moved three or more buildings — a book moved
   *  in one go, which is a different business event from bleeding work. */
  bulk_handover_60d: boolean;
  /** The most recent one, so "this week" reads differently from "eight weeks ago". */
  last_loss_on: string | null;
  /** Why — the sub-change reasons behind those losses, in list order (#667).
   *  Only reasons that count as a loss appear. */
  loss_reason_codes: string[];
  lead_am_eid: string | null;
  lead_am_name: string | null;
  lead_market: string | null;
  capabilities: string[];
  tier_1: boolean;
}

export function useSubcontractorTiers(): {
  rows: SubTierRow[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [rows, setRows] = useState<SubTierRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: e1 } = await supabase
          .from('v_subcontractor_tier')
          .select(
            'provider_id, display_name, tier, active_facility_count, days_with_tcs, tenure_is_estimate, facilities_lost_60d, loss_events_60d, bulk_handover_60d, last_loss_on, loss_reason_codes, lead_am_eid, lead_market, capabilities, tier_1',
          )
          .order('tier', { ascending: true })
          .order('display_name', { ascending: true });
        if (e1) throw e1;

        const base = (data ?? []) as Array<
          Omit<SubTierRow, 'lead_am_name' | 'capabilities' | 'loss_reason_codes'> & {
            capabilities: string[] | null;
            loss_reason_codes: string[] | null;
          }
        >;
        const names = await fetchAmNameMap(base.map((r) => r.lead_am_eid));
        if (cancelled) return;

        setRows(
          base.map((r) => ({
            ...r,
            capabilities: r.capabilities ?? [],
            loss_reason_codes: r.loss_reason_codes ?? [],
            tier_1: Boolean(r.tier_1),
            bulk_handover_60d: Boolean(r.bulk_handover_60d),
            lead_am_name: r.lead_am_eid ? (names.get(r.lead_am_eid) ?? null) : null,
          })),
        );
      } catch (e) {
        if (!cancelled) setError(errorMessage(e, 'Could not load the subcontractor tier roster.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { rows, loading, error, refetch: useCallback(() => setTick((n) => n + 1), []) };
}
